import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrganizationId } from '../utils/organizationContext';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  Card,
  CardContent,
  Tooltip,
  TablePagination,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  DialogContentText,
  TableSortLabel,
} from '@mui/material';
import { Edit, Phone, Email, RemoveRedEye as ViewIcon, Add, DirectionsCar, School as SchoolIcon, Search as SearchIcon, FilterList as FilterIcon, Group as GroupIcon, Warning as WarningIcon, CheckCircle as CheckCircleIcon, DirectionsCar as CarIcon, Download as DownloadIcon } from '@mui/icons-material';

import { format } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axios from 'axios';

const FollowUpDashboard = () => {
  const { organizationSlug } = useParams();
  const { user } = useAuth();
  const [currentOrgId, setCurrentOrgId] = useState(null);
  const [firstTimers, setFirstTimers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalStats, setTotalStats] = useState({
    total: 0,
    pending: 0,
    completed: 0
  });

  // Load organization ID from user or URL slug
  useEffect(() => {
    const loadOrgId = async () => {
      // If user has organization_id, use it directly
      if (user?.organization_id) {
        setCurrentOrgId(user.organization_id);
        return;
      }
      
      // If user is super-admin and we have organization slug in URL, get org ID
      if (user?.role === 'system_admin' && !user?.organization_id && organizationSlug) {
        const orgId = await getOrganizationId(organizationSlug);
        if (orgId) {
          setCurrentOrgId(orgId);
        }
      }
    };
    loadOrgId();
  }, [user, organizationSlug]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFirstTimers, setFilteredFirstTimers] = useState([]);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [debouncedDateRange, setDebouncedDateRange] = useState([null, null]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'contacted':
        return 'info';
      case 'unreachable':
        return 'error';
      case 'pending':
      default:
        return 'warning';
    }
  };

  const getStatusChipStyle = (status) => {
    switch (status) {
      case 'completed':
        return {
          backgroundColor: '#10b981',
          color: 'white',
          fontWeight: 'bold',
          textTransform: 'capitalize',
          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
        };
      case 'contacted':
        return {
          backgroundColor: '#3b82f6',
          color: 'white',
          fontWeight: 'bold',
          textTransform: 'capitalize',
          boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
        };
      case 'unreachable':
        return {
          backgroundColor: '#ef4444',
          color: 'white',
          fontWeight: 'bold',
          textTransform: 'capitalize',
          boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
        };
      case 'pending':
      default:
        return {
          backgroundColor: '#f59e0b',
          color: 'white',
          fontWeight: 'bold',
          textTransform: 'capitalize',
          boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
        };
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setLoggedInUser(user);
  }, []);

  const fetchFirstTimers = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Format dates to YYYY-MM-DD for the backend
      const formatDate = (date) => {
        if (!date) return null;
        return date.toISOString().split('T')[0];
      };

      const searchParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...(searchQuery && { search: searchQuery }),
        ...(dateRange[0] && { startDate: formatDate(dateRange[0]) }),
        ...(dateRange[1] && { endDate: formatDate(dateRange[1]) })
      });

      // For super-admins viewing organization-specific dashboard, add organization_id
      // For regular organization admins, the backend filters by their organization_id automatically
      // But we can also explicitly add it if we have currentOrgId
      if (currentOrgId && (user?.role === 'system_admin' || user?.organization_id === currentOrgId)) {
        searchParams.append('organization_id', currentOrgId);
      }

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/follow-up/all?${searchParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const { data } = response;

      if (!data) {
        throw new Error('No data received from server');
      }

      // Process the data to handle service unit visibility
      const processedData = (data.data || []).map(timer => {
        if (!timer.last_follow_up_date) {
          return {
            ...timer,
            last_follow_up_date: null,
            last_followed_by: null,
            follow_up_status: 'pending'
          };
        }

        const [firstName, lastName] = (timer.last_followed_by || '').split(' ');
        const isFollowedByUserUnit = timer.followed_by_unit === loggedInUser?.service_unit;
        const isFollowedByUser = firstName === loggedInUser?.first_name && lastName === loggedInUser?.last_name;


        if (isFollowedByUserUnit || isFollowedByUser) {
          return timer;
        }

        return {
          ...timer,
          last_follow_up_date: null,
          last_followed_by: null,
          follow_up_status: 'pending'
        };
      });

      setFirstTimers(processedData);
      setFilteredFirstTimers(processedData);
      setTotalCount(data.total || 0);
      setTotalStats({
        total: data.total || 0,
        pending: data.totalPending || 0,
        completed: data.totalCompleted || 0,
        transportation: data.totalTransportation || 0
      });
    } catch (error) {
      console.error('Error fetching first timers:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch first timers');
      setFirstTimers([]);
      setFilteredFirstTimers([]);
      setTotalStats({
        total: 0,
        pending: 0,
        completed: 0,
        transportation: 0
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, dateRange, loggedInUser, navigate, user, currentOrgId]);

  const applyFilters = useCallback(() => {
    let filtered = [...firstTimers];
    if (dateRange[0] && dateRange[1]) {
      filtered = filtered.filter(ft => {
        const visitDate = new Date(ft.service_date);
        return visitDate >= dateRange[0] && visitDate <= dateRange[1];
      });
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(ft => 
        ft.first_name.toLowerCase().includes(query) ||
        ft.last_name.toLowerCase().includes(query) ||
        ft.phone_number.toLowerCase().includes(query) ||
        (ft.email && ft.email.toLowerCase().includes(query))
      );
    }

    setFilteredFirstTimers(filtered);
  }, [firstTimers, dateRange, searchQuery]);

  // Apply local filters immediately for responsive UI
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Debounce search query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Debounce date range
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedDateRange(dateRange);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [dateRange]);

  // Fetch data when debounced values change or organization ID changes
  useEffect(() => {
    if (currentOrgId || user?.organization_id) {
      fetchFirstTimers();
    }
  }, [fetchFirstTimers, debouncedSearchQuery, debouncedDateRange, page, rowsPerPage, currentOrgId, user?.organization_id]);

  const handleViewClick = (firstTimer, event) => {
    event.stopPropagation();
    // Include organization slug in navigation path
    const path = organizationSlug 
      ? `/${organizationSlug}/first-timer/${firstTimer.id}`
      : `/first-timer/${firstTimer.id}`;
    navigate(path);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    setFilteredFirstTimers(firstTimers);
  }, [firstTimers]);

  const handleSearch = (value) => {
    setSearchQuery(value);
    setPage(0);
  };

  const handleDateRangeChange = (index, value) => {
    setDateRange(prev => {
      const newRange = [...prev];
      newRange[index] = value;
      return newRange;
    });
    setPage(0);
  };

  const handleStartDateChange = (newValue) => {
    setPage(0);
    setDateRange([newValue, dateRange[1]]);
  };

  const handleEndDateChange = (newValue) => {
    setPage(0);
    setDateRange([dateRange[0], newValue]);
  };

  const handleSearchChange = (e) => {
    setPage(0);
    setSearchQuery(e.target.value);
  };

  const handleClearFilters = () => {
    setDateRange([null, null]);
    setSearchQuery('');
    setPage(0);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setExportError('');

      // Format dates to YYYY-MM-DD for the backend
      const formatDate = (date) => {
        if (!date) return null;
        return date.toISOString().split('T')[0];
      };

      const searchParams = new URLSearchParams({
        ...(searchQuery && { search: searchQuery }),
        ...(dateRange[0] && { startDate: formatDate(dateRange[0]) }),
        ...(dateRange[1] && { endDate: formatDate(dateRange[1]) })
      });

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      console.log('Exporting with params:', searchParams.toString());
      console.log('API URL:', `${process.env.REACT_APP_API_URL}/api/follow-up/export?${searchParams}`);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/follow-up/export?${searchParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob' // Important for file downloads
        }
      );

      console.log('Export response:', response);

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'wcito-first-timers.csv';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else {
        // Fallback: create filename with current timestamp
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, '-').split('T')[0] + '-' + 
                         now.toTimeString().split(' ')[0].replace(/:/g, '-');
        filename = `wcito-first-timers-${timestamp}.csv`;
      }
      
      console.log('Downloading file:', filename);
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Export completed successfully');

    } catch (error) {
      console.error('Export error:', error);
      console.error('Error response:', error.response);
      setExportError(error.response?.data?.error || 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ 
      mt: 4, 
      mb: 4,
      background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
      minHeight: '100vh',
      py: 2
    }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ 
            background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            Follow Up Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Manage and track first timers' follow-up status
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={isExporting}
            sx={{
              borderColor: '#DC2626',
              color: '#DC2626',
              '&:hover': {
                borderColor: '#B91C1C',
                backgroundColor: '#fef2f2',
              },
              px: 3,
              py: 1.5,
              borderRadius: 2,
            }}
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            component={RouterLink}
            to={organizationSlug ? `/${organizationSlug}/add-first-timer` : "/add-first-timer"}
            sx={{
              background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%)',
              },
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(220, 38, 38, 0.3)'
            }}
          >
            Add First Timer
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ 
            p: 2, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(220, 38, 38, 0.3)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(220, 38, 38, 0.4)'
            }
          }}>
            <GroupIcon sx={{ fontSize: 28, color: 'white', mb: 1, opacity: 0.9 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1, fontSize: '0.9rem' }}>Total First Timers</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>{totalStats.total}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ 
            p: 2, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(245, 158, 11, 0.3)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(245, 158, 11, 0.4)'
            }
          }}>
            <WarningIcon sx={{ fontSize: 28, color: 'white', mb: 1, opacity: 0.9 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1, fontSize: '0.9rem' }}>Pending Follow-ups</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>{totalStats.pending}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ 
            p: 2, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(16, 185, 129, 0.4)'
            }
          }}>
            <CheckCircleIcon sx={{ fontSize: 28, color: 'white', mb: 1, opacity: 0.9 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1, fontSize: '0.9rem' }}>Completed</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>{totalStats.completed}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ 
            p: 2, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #991B1B 0%, #7F1D1D 100%)',
            color: 'white',
            boxShadow: '0px 4px 20px rgba(153, 27, 27, 0.3)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(153, 27, 27, 0.4)'
            }
          }}>
            <CarIcon sx={{ fontSize: 28, color: 'white', mb: 1, opacity: 0.9 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1, fontSize: '0.9rem' }}>Need Transportation</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
              {totalStats.transportation}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ 
        p: 3, 
        mb: 3,
        border: '1px solid #fecaca',
        background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 5%, #ffffff 100%)'
      }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={2} alignItems="center">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={dateRange[0]}
                  onChange={handleStartDateChange}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#DC2626',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#DC2626',
                          },
                        },
                      }
                    }
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={dateRange[1]}
                  onChange={handleEndDateChange}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        '& .MuiOutlinedInput-root': {
                                                  '&:hover fieldset': {
                          borderColor: '#DC2626',
                        },
                          '&.Mui-focused fieldset': {
                            borderColor: '#DC2626',
                          },
                        },
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search by name, phone, or email..."
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#DC2626',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#DC2626',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#DC2626' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              fullWidth
              size="small"
              sx={{ 
                fontSize: '0.75rem',
                textTransform: 'none',
                borderColor: '#DC2626',
                color: '#DC2626',
                '&:hover': {
                  borderColor: '#991B1B',
                  backgroundColor: '#fef2f2'
                }
              }}
              disabled={!dateRange[0] && !dateRange[1] && !searchQuery}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ 
          mb: 2,
          border: '1px solid #fecaca',
          '& .MuiAlert-icon': {
            color: '#DC2626'
          }
        }}>
          {error}
        </Alert>
      )}

      {exportError && (
        <Alert severity="error" sx={{ 
          mb: 2,
          border: '1px solid #fecaca',
          '& .MuiAlert-icon': {
            color: '#DC2626'
          }
        }}>
          {exportError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress sx={{ color: '#DC2626' }} />
        </Box>
      ) : filteredFirstTimers.length === 0 ? (
        <Alert severity="info" sx={{ 
          mb: 2,
          border: '1px solid #bfdbfe',
          '& .MuiAlert-icon': {
            color: '#3b82f6'
          }
        }}>
          No first timer records found
        </Alert>
      ) : isMobile ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              sx={{
                '& .MuiTablePagination-selectIcon': {
                  color: '#DC2626',
                },
                '& .MuiTablePagination-select': {
                  '&:focus': {
                    backgroundColor: '#fef2f2',
                  },
                },
              }}
            />
          </Box>
          <Stack spacing={2}>
            {filteredFirstTimers.map((timer, index) => (
              <Card key={timer.id} sx={{
                border: '1px solid #fecaca',
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(220, 38, 38, 0.15)',
                  borderColor: '#DC2626'
                }
              }}>
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {timer.first_name} {timer.last_name}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      Service Date: {format(new Date(`${timer.service_date.split('T')[0]}T12:00:00Z`), 'MMM d, yyyy')}

                    </Typography>
                    
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size="small" href={`tel:${timer.phone_number}`}>
                          <Phone fontSize="small" />
                        </IconButton>
                        {timer.phone_number}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <IconButton size="small" href={`mailto:${timer.email}`}>
                          <Email fontSize="small" />
                        </IconButton>
                        {timer.email}
                      </Box>
                      {timer.needs_transportation && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <DirectionsCar fontSize="small" sx={{ color: '#f59e0b' }} />
                          <Typography variant="body2" sx={{ 
                            color: '#f59e0b', 
                            fontWeight: 'bold',
                            backgroundColor: '#fef3c7',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem'
                          }}>
                            Needs Transportation
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    <Box>
                      <Chip 
                        label={timer.follow_up_status}
                        size="small"
                        sx={{ 
                          mb: 1,
                          ...getStatusChipStyle(timer.follow_up_status)
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Last Follow-up: {timer.last_follow_up_date ? 
                          format(new Date(timer.last_follow_up_date), 'MMM d, yyyy') : 
                          'No follow-up yet'
                        }
                      </Typography>
                      {timer.last_followed_by && (
                        <Typography variant="body2" color="text.secondary">
                          Followed By: {timer.last_followed_by}
                        </Typography>
                      )}
                    </Box>
                    
                    <Stack direction="row" spacing={1}>
                      <IconButton 
                        size="small" 
                        component={RouterLink} 
                        to={organizationSlug ? `/${organizationSlug}/first-timer/${timer.id}` : `/first-timer/${timer.id}`}
                        sx={{
                          color: '#DC2626',
                          '&:hover': {
                            backgroundColor: '#fef2f2',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              sx={{
                '& .MuiTablePagination-selectIcon': {
                  color: '#DC2626',
                },
                '& .MuiTablePagination-select': {
                  '&:focus': {
                    backgroundColor: '#fef2f2',
                  },
                },
              }}
            />
          </Box>
        </>
      ) : (
        <TableContainer sx={{ 
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            />
          </Box>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid #e5e7eb' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid #e5e7eb' }}>Service Date</TableCell>
                <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid #e5e7eb' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid #e5e7eb' }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid #e5e7eb' }}>Last Follow-up</TableCell>
                <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid #e5e7eb' }}>Followed By</TableCell>
                <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid #e5e7eb' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFirstTimers.map((timer) => (
                <TableRow 
                  key={timer.id}
                  hover
                  onClick={() => {
                    const path = organizationSlug 
                      ? `/${organizationSlug}/first-timer/${timer.id}`
                      : `/first-timer/${timer.id}`;
                    navigate(path);
                  }}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:nth-of-type(even)': {
                      backgroundColor: '#fafafa'
                    },
                    '&:hover': {
                      backgroundColor: '#fef2f2',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(220, 38, 38, 0.1)'
                    },
                    '& .MuiIconButton-root': {
                      position: 'relative',
                      zIndex: 2
                    }
                  }}
                >
                  <TableCell>
                    <Chip 
                      label={timer.follow_up_status} 
                      size="small"
                      sx={{
                        ...getStatusChipStyle(timer.follow_up_status)
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {format(new Date(`${timer.service_date.split('T')[0]}T12:00:00Z`), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {timer.first_name} {timer.last_name}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size="small" href={`tel:${timer.phone_number}`}>
                          <Phone fontSize="small" />
                        </IconButton>
                        {timer.phone_number}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <IconButton size="small" href={`mailto:${timer.email}`}>
                          <Email fontSize="small" />
                        </IconButton>
                        {timer.email}
                      </Box>
                      {timer.needs_transportation && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <DirectionsCar fontSize="small" sx={{ color: '#f59e0b' }} />
                          <Typography variant="body2" sx={{ 
                            color: '#f59e0b', 
                            fontWeight: 'bold',
                            backgroundColor: '#fef3c7',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem'
                          }}>
                            Needs Transportation
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {timer.last_follow_up_date ? 
                      format(new Date(timer.last_follow_up_date), 'MMM d, yyyy') : 
                      'No follow-up yet'
                    }
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {timer.last_followed_by || '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      onClick={(e) => handleViewClick(timer, e)}
                      sx={{
                        color: '#DC2626',
                        '&:hover': {
                          backgroundColor: '#fef2f2',
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              sx={{
                '& .MuiTablePagination-selectIcon': {
                  color: '#DC2626',
                },
                '& .MuiTablePagination-select': {
                  '&:focus': {
                    backgroundColor: '#fef2f2',
                  },
                },
              }}
            />
          </Box>
        </TableContainer>
      )}
    </Container>
  );
};

export default FollowUpDashboard; 